if (typeof debounce === 'undefined') {
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}
/**
 *  @class
 *  @function ThemeHeader
 */

if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    constructor() {
      super();

    }
    connectedCallback() {
      this.header_section = document.querySelector('.header-section');
      this.menu = this.querySelector('#mobile-menu');
      this.toggle = this.querySelector('.mobile-toggle-wrapper');

      document.addEventListener('keyup', (e) => {
        if (e.code) {
          if (e.code.toUpperCase() === 'ESCAPE') {
            this.toggle?.removeAttribute('open');
            this.toggle?.classList.remove('active');
          }
        }
      });
      if (this.classList.contains('header-sticky--active')) {
        document.body.classList.add('header-sticky--active');
      }
      this.toggle?.querySelector('.mobile-toggle')?.addEventListener('click', (e) => {
        if (this.toggle.classList.contains('active')) {
          e.preventDefault();
          document.body.classList.remove('overflow-hidden');
          this.toggle.classList.remove('active');
          this.closeAnimation(this.toggle);
        } else {
          document.body.classList.add('overflow-hidden');
          setTimeout(() => {
            this.toggle.classList.add('active');
          });
        }
      });

      // Single rAF-throttled handler for all layout reads
      this._a_bar = document.querySelector('.announcement-bar-section');
      this._scrollRafId = null;
      this._boundScrollHandler = () => {
        if (this._scrollRafId) return;
        this._scrollRafId = requestAnimationFrame(() => {
          this._scrollRafId = null;
          this.#updateLayoutVars();
        });
      };
      window.addEventListener('scroll', this._boundScrollHandler, { passive: true });
      window.addEventListener('resize', debounce(this._boundScrollHandler, 100));
      window.addEventListener('load', this._boundScrollHandler);

      if ('ResizeObserver' in window) {
        this._layoutObserver = new ResizeObserver(this._boundScrollHandler);
        const inner = this.querySelector('.header--inner');
        if (inner) this._layoutObserver.observe(inner);
        if (this._a_bar) this._layoutObserver.observe(this._a_bar);
      }

      this._boundScrollHandler();

      // Mobile Navigation transparent header support.
      setTimeout(() => {

        this.mobile_nav = document.querySelector('.header-mobile-navigation');

        if (this.mobile_nav && this.classList.contains('transparent--true')) {
          this.mobile_nav.classList.add('is-fixed');
        }
      }, 100);
      // Buttons.
      this.menu?.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
      this.menu?.querySelectorAll('.parent-link-back--button').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
    }
    #measureHeaderHeight() {
      const inner = this.querySelector('.header--inner');
      let height = 0;

      if (inner) {
        height = Math.round(inner.getBoundingClientRect().height);
      }
      if (height <= 0) {
        height = Math.round(this.getBoundingClientRect().height);
      }
      if (height <= 0 && this.header_section) {
        height = Math.round(this.header_section.getBoundingClientRect().height);
      }

      return height;
    }
    #updateLayoutVars() {
      const scrollY = window.scrollY;
      const headerTop = this.header_section?.getBoundingClientRect().top ?? 0;
      const headerHeight = this.#measureHeaderHeight();
      const isSticky = this.classList.contains('header-sticky--active');
      let stickyOffset = 0;

      if (isSticky) {
        stickyOffset = parseInt(this.getBoundingClientRect().top, 10) + document.documentElement.scrollTop;
      }

      let announcementHeight = 0;
      if (this._a_bar) {
        announcementHeight = this._a_bar.clientHeight;
      }

      if (isSticky) {
        this.classList.toggle('is-sticky', scrollY >= stickyOffset && scrollY > 0);
      }

      document.documentElement.style.setProperty('--header-offset', headerTop + 'px');

      if (headerHeight > 0) {
        document.documentElement.style.setProperty('--header-height', headerHeight + 'px');
      }

      if (this._a_bar && announcementHeight > 0) {
        document.documentElement.style.setProperty('--announcement-height', announcementHeight + 'px');
      }
    }
    setStickyClass() {
      if (this.classList.contains('header-sticky--active')) {
        let offset = parseInt(this.getBoundingClientRect().top, 10) + document.documentElement.scrollTop;
        this.classList.toggle('is-sticky', window.scrollY >= offset && window.scrollY > 0);
      }
    }
    setAnnouncementHeight() {
      const a_bar = document.querySelector('.announcement-bar-section');
      let h = a_bar.clientHeight;
      document.documentElement.style.setProperty('--announcement-height', h + 'px');
    }
    setHeaderOffset() {
      let h = this.header_section.getBoundingClientRect().top;
      document.documentElement.style.setProperty('--header-offset', h + 'px');
    }
    setHeaderHeight() {
      this.#updateLayoutVars();
    }
    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const parentMenuElement = detailsElement.closest('.link-container');
      const isOpen = detailsElement.hasAttribute('open');
      if (this.querySelector('.parent-link-back--button')) {
        this.menu.scrollTop = 0;
      }
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
      }, 100);
    }
    onCloseButtonClick(event) {
      event.preventDefault();
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }
    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('menu-opening');
      this.closeAnimation(detailsElement);
    }
    closeAnimation(detailsElement) {
      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }
  }
  customElements.define('theme-header', ThemeHeader);
}

/**
 *  @class
 *  @function FullMenu
 */
if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    constructor() {
      super();

      this.submenus = this.querySelectorAll('.thb-full-menu>.menu-item-has-children:not(.menu-item-has-megamenu)>.sub-menu');
    }
    connectedCallback() {
      if (!this.submenus.length) {
        return;
      }
      const _this = this;
      // resize on initial load
      document.fonts.ready.then(function() {
        window.addEventListener('resize', debounce(function() {
          _this.resizeSubMenus();
        }, 100));
      });

    }
    resizeSubMenus() {
      const viewportWidth = window.innerWidth;
      const measurements = [];

      // Read phase - batch all layout reads
      this.submenus.forEach((submenu) => {
        let sub_submenus = submenu.querySelectorAll(':scope >.menu-item-has-children>.sub-menu');

        sub_submenus.forEach((sub_submenu) => {
          let w = sub_submenu.offsetWidth,
            l = sub_submenu.parentElement.getBoundingClientRect().left + sub_submenu.parentElement.offsetWidth,
            total = w + l;
          measurements.push({ parent: sub_submenu.parentElement, total: total });
        });
      });

      // Write phase - batch all DOM writes
      measurements.forEach(({ parent, total }) => {
        if (total > viewportWidth) {
          parent.classList.add('left-submenu');
        } else {
          parent.classList.remove('left-submenu');
        }
      });
    }
  }
  customElements.define('full-menu', FullMenu);
}